import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';

/**
 * useAllMessagesSubscription — Family (CLAUDE.md §6 madde 5)
 *
 * Global "any conversation I'm in" Realtime subscription.
 *
 * Why we need this (Bug 10, Oturum 7): the per-conversation hook only fires
 * when the user is *inside* that chat. From Home, the user has no live signal
 * that a new message arrived — the contact list went stale until manual reload.
 *
 * Strategy:
 *   1. Subscribe to ALL `messages` INSERTs (no server-side filter, RLS still
 *      gates which payloads reach us — non-members get nothing).
 *   2. On each event, invalidate the `contact-last-messages` query → ContactList
 *      re-fetches preview + unread badge + tab title flash.
 *   3. Also surface a "new message" pulse via `document.title` so the tab tab
 *      gets attention even when the browser tab is backgrounded.
 *
 * Cleanup: channel.unsubscribe() on unmount or session change.
 */
export function useAllMessagesSubscription(session: Session | null): void {
  const queryClient = useQueryClient();
  const titleResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const myId = session?.user.id ?? null;
    if (!myId) return;

    const supabase = getSupabase();
    const channel = supabase
      .channel('global-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        (payload) => {
          // RLS already filtered: if we got the event, we're a member.
          // Skip own messages — tab flash only for incoming.
          const senderId = (payload.new as { sender_id?: string }).sender_id;
          const isMine = senderId === myId;

          void queryClient.invalidateQueries({
            queryKey: ['contact-last-messages', myId],
          });

          if (isMine) return;
          flashTabTitle();
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_translations',
        },
        () => {
          void queryClient.invalidateQueries({
            queryKey: ['contact-last-messages', myId],
          });
        },
      )
      .subscribe();

    function flashTabTitle() {
      if (typeof document === 'undefined') return;
      // Strip any existing prefix to avoid stacking emojis.
      const base = document.title.replace(/^[🔴●•]\s*/u, '');
      document.title = `🔴 ${base}`;
      if (titleResetRef.current) clearTimeout(titleResetRef.current);
      // Reset 4 seconds later so the user can briefly see it, then it clears.
      titleResetRef.current = setTimeout(() => {
        document.title = base;
      }, 4000);
    }

    // Reset title on tab focus.
    function onFocus() {
      if (typeof document === 'undefined') return;
      document.title = document.title.replace(/^[🔴●•]\s*/u, '');
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
    }

    return () => {
      void channel.unsubscribe();
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
      if (titleResetRef.current) clearTimeout(titleResetRef.current);
    };
  }, [session, queryClient]);
}
